import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Rocket } from 'lucide-react'
import { api, type Publish, type PublishStatus } from '../lib/api'
import { TYPES } from '../lib/types'
import { Notice, Spinner, StatusChip } from './ui'

const ACTIVE = new Set(['queued', 'building'])

export function usePublishStatus() {
  return useQuery({
    queryKey: ['publish', 'status'],
    queryFn: () => api.get<PublishStatus>('/publish/status'),
    // Poll while a build is running so the badge flips to success on its own.
    refetchInterval: (q) => (q.state.data?.last && ACTIVE.has(q.state.data.last.status) ? 5000 : 60_000),
  })
}

export function changedSummary(changed: PublishStatus['changed']): string {
  const parts = Object.entries(changed).map(([t, n]) => {
    const type = TYPES[t as keyof typeof TYPES]
    const name = t === 'settings' ? 'site copy' : type ? (n === 1 ? type.singular : type.label.toLowerCase()) : t
    return `${n} ${name}`
  })
  return parts.length ? parts.join(', ') : 'nothing'
}

/** Header button: rebuilds the public site from everything that is published. */
export default function PublishButton() {
  const qc = useQueryClient()
  const status = usePublishStatus()
  const [open, setOpen] = useState(false)
  const publish = useMutation({
    mutationFn: () => api.post<{ item: Publish }>('/publish'),
    onSettled: () => qc.invalidateQueries({ queryKey: ['publish'] }),
  })

  const last = status.data?.last
  const running = last && ACTIVE.has(last.status)
  const changes = Object.values(status.data?.changed ?? {}).reduce((a, b) => a + (b ?? 0), 0)

  return (
    <div className="relative">
      <button className="btn-primary" onClick={() => setOpen(!open)}>
        {running ? <Spinner /> : <Rocket className="h-3.5 w-3.5" />}
        {running ? 'Publishing…' : 'Publish'}
        {!running && changes > 0 && <span className="rounded-full bg-accent-fg/20 px-1.5 text-[11px]">{changes}</span>}
      </button>

      {open && (
        <div className="card absolute end-0 top-11 z-20 w-80 space-y-3 p-4 shadow-xl">
          <p className="text-sm font-semibold">Publish the site</p>
          <p className="text-xs text-muted">
            Rebuilds mlopsmena.com from every <b>published</b> item. Takes about 3 minutes. Drafts and archived
            items stay private.
          </p>
          {status.data && (
            <p className="text-xs text-muted">
              Changed since the last successful publish: <b className="text-fg">{changedSummary(status.data.changed)}</b>
            </p>
          )}
          {last && (
            <div className="flex items-center justify-between gap-2 text-xs text-muted">
              <span>Last: <StatusChip status={last.status} /></span>
              {last.runUrl && (
                <a className="inline-flex items-center gap-1 text-accent" href={last.runUrl} target="_blank" rel="noreferrer">
                  build log <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
          {publish.isError && <Notice>{(publish.error as Error).message}</Notice>}
          {publish.isSuccess && <Notice kind="ok">Started. This button shows progress.</Notice>}
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={() => setOpen(false)}>Close</button>
            <button className="btn-primary" disabled={Boolean(running) || publish.isPending} onClick={() => publish.mutate()}>
              {publish.isPending && <Spinner />} Publish now
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
